import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Autocomplete,
    MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSnackbar } from 'src/components/snackbar';
import { useDispatch, useSelector } from 'react-redux';
import { selectFilteredData, selectProjects } from 'src/redux/selectors';
import { addGuarantee } from 'src/redux/slices/cautionSlice';
import { useServerRequest } from 'src/hooks/useServerRequest';
import { useLocales } from 'src/locales';

interface AddGuaranteeProps {
    open: boolean;
    onClose: () => void;
    projectId: number;
}

const AddGuarantee: React.FC<AddGuaranteeProps> = ({ open, onClose, projectId }) => {
    const filteredData = useSelector(selectFilteredData);
    const projects = useSelector(selectProjects);
    const [company, setCompany] = useState<number | null>(null);
    const [rib, setRib] = useState<number | null>(null);
    const [type, setType] = useState<string | null>(null);
    const [amount, setAmount] = useState<string>('');
    const [submissionDate, setSubmissionDate] = useState<Date | null>(null);
    const [note, setNote] = useState<string>('');
    const [ribOptions, setRibOptions] = useState<{ id: number; label: string }[]>([]);
    const [projId, setProjId] = useState<number | null>(projectId || null);
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { makeRequest } = useServerRequest();
    const { t } = useLocales();


    const types = [
        { value: 'soumission', label: t('cautions.guarantees.types.soumission') },
        { value: 'execution', label: t('cautions.guarantees.types.execution') },
        { value: 'maintenance', label: t('cautions.guarantees.types.maintenance') },
        { value: 'avance', label: t('cautions.guarantees.types.avance') },
    ];

    const handleCompanyChange = (event: any, newValue: { id: number; label: string } | null) => {
        setCompany(newValue?.id || null);

        if (newValue) {
            const selectedCompany = filteredData.find((comp) => comp.id === newValue.id);

            if (selectedCompany) {
                const ribs = selectedCompany.ribs.map((rb) => ({
                    id: rb.id,
                    label: `${rb.rib} - ${rb.bref}`,
                }));
                setRibOptions(ribs);
            } else {
                setRibOptions([]);
            }
        } else {
            setRibOptions([]);
        }
    };

    const handleRibChange = (event: any, newValue: { id: number; label: string } | null) => {
        setRib(newValue?.id || null);
    };

    const handleSubmit = async () => {
        if (!company || !rib || !type || !amount || !submissionDate) {
            enqueueSnackbar(t('cautions.projects.inputs.alert-reuirements'), { variant: 'error' });
            return;
        }

        try {
            const data = {
                company_id: company,
                rib_id: rib,
                type,
                amount: parseFloat(amount),
                submission_date: submissionDate ? submissionDate.toISOString().split('T')[0] : null,
                project_id: projId,
                note,
            };

            const response = await makeRequest(
                '/guarantees',
                { method: 'POST', data },
                t('cautions.guarantees.inputs.alert-success')
            );

            if (response.success && response.data) {
                dispatch(addGuarantee(response.data.guarantee));
                onClose();
            }
        } catch (error) {
            enqueueSnackbar(t('cautions.guarantees.inputs.alert-error'), { variant: 'error' });
        }
    };

    const companyOptions = filteredData.map((comp) => ({
        id: comp.id,
        label: comp.name,
    }));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('cautions.guarantees.add')}</DialogTitle>
            <DialogContent sx={{ marginTop: 0 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={companyOptions}
                            getOptionLabel={(option) => option.label}
                            value={companyOptions.find((comp) => comp.id === company) || null}
                            onChange={handleCompanyChange}
                            renderInput={(params) => <TextField {...params} label={t('common.input.selectCompany')} />}
                            sx={{
                                marginTop: 1
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={ribOptions}
                            getOptionLabel={(option) => option.label}
                            value={ribOptions.find((rb) => rb.id === rib) || null}
                            onChange={handleRibChange}
                            renderInput={(params) => <TextField {...params} label={t('common.input.selectRib')} />}
                            sx={{
                                marginTop: 1
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={types}
                            getOptionLabel={(option) => option.label}
                            value={types.find((typeOption) => typeOption.value === type) || null}
                            onChange={(event, newValue) => setType(newValue?.value || null)}
                            renderInput={(params) => <TextField {...params} label={t('cautions.guarantees.inputs.type.label')} />}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            select
                            label={t('cautions.guarantees.inputs.project.label')}
                            value={projId || ''}
                            onChange={(e) => setProjId(Number(e.target.value))}
                            required
                        >
                            {projects.map((project) => (
                                <MenuItem key={project.id} value={project.id}>
                                    {project.number}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('common.input.amount.label')}
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder={t('common.input.amount.holder')}
                            type="number"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DatePicker
                            label={t('cautions.guarantees.submissionDate')}
                            value={submissionDate}
                            onChange={(newValue) => setSubmissionDate(newValue)}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('common.input.note.label')}
                            multiline
                            rows={3}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined" color="inherit">
                    {t('common.button.action.cancel')}
                </Button>
                <Button onClick={handleSubmit} variant="contained">
                    {t('common.button.action.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddGuarantee;
